import {
  Component,
  computed,
  inject,
  OnInit,
  Output,
  EventEmitter,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, map } from 'rxjs';
import { Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PlantStatus, PlantStatusEnum } from '@anessa/shared-models';
import { I18nPipe } from '../../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../../shared/i18n/i18n-scoped.service';
import { I18nScopeReadyDirective } from '../../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../../shared/i18n/i18n-scope.provider';
import { ProjectService } from '../../../shared/services/project/project.service';
import { Project } from '../../../shared/services/project/project.model';
import { Plant } from '../../../features/dashboard/services/plant/plant.model';
import { UserService } from '../../../shared/services/permissions-management/user.service';
import { RoleService } from '../../../shared/services/permissions-management/role.service';
import { UserPlantRoleService } from '../../../shared/services/permissions-management/user-plant-role.service';
import { MyAuthService } from '../../../auth/auth.service';
import { ErrorService } from '../../../shared/services/error/error.service';
import { Role } from '../../../shared/interfaces/role.interface';
import { UserPlantRole } from '../../../shared/interfaces/user-plant-role.interface';
import {
  compareFavoritesFirst,
  FavoriteProjectsService,
} from '../../../shared/services/favorite-projects/favorite-projects.service';
import { isOpenInNewTabClick } from '../../../shared/tab-view/tab-click.util';

const ERROR_SOURCE = 'PlantSwitcher';

interface PlantSwitcherItem {
  id: string;
  name: string;
  isMultiPlant: boolean;
  isHealthLoading: boolean;
  status: PlantStatus | null;
  role: string;
  plantMeta?: Plant;
  plantIds?: string[];
}

@Component({
  selector: 'app-plant-switcher',
  imports: [MatIcon, MatProgressSpinner, I18nPipe, I18nScopeReadyDirective],
  templateUrl: './plant-switcher.html',
  styleUrl: './plant-switcher.scss',
  providers: [provideI18nScope('ui/topbar/plant-switcher'), I18nScopedService],
})
export class PlantSwitcherComponent implements OnInit {
  @Output() readonly closed = new EventEmitter<void>();

  protected readonly projectService = inject(ProjectService);
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly roleService = inject(RoleService);
  private readonly userPlantRoleService = inject(UserPlantRoleService);
  private readonly authService = inject(MyAuthService);
  private readonly errorService = inject(ErrorService);
  private readonly favorites = inject(FavoriteProjectsService);

  protected readonly plantStatusEnum = PlantStatusEnum;

  private readonly allProjects = signal<PlantSwitcherItem[]>([]);
  private readonly plantRoleMap = signal<Map<string, string>>(new Map());
  private readonly allRoles = signal<Role[]>([]);

  protected readonly favoriteIds = this.favorites.favoriteIds;

  protected readonly plants = computed(() =>
    this.allProjects()
      .filter((project) => {
        if (project.plantMeta?.id) {
          return this.plantRoleMap().has(project.plantMeta.id);
        }

        if (project.plantIds?.length) {
          return project.plantIds.every((id) => this.plantRoleMap().has(id));
        }

        return false;
      })
      .sort(compareFavoritesFirst(this.favoriteIds())),
  );

  constructor() {
    this.projectService
      .getProjectsForSwitcher()
      .pipe(takeUntilDestroyed())
      .pipe(map((projects) => projects.map((p) => this.toSwitcherItem(p))))
      .subscribe((items) => this.allProjects.set(this.applyRoles(items)));
  }

  ngOnInit(): void {
    void this.favorites.ensureLoaded();
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.fetchRoles();
    await this.fetchCurrUserRoles();
  }

  private async fetchRoles(): Promise<void> {
    try {
      const roles = await firstValueFrom(this.roleService.getAllRoles());
      this.allRoles.set(roles ?? []);
    } catch (err) {
      this.errorService.report(err, {
        source: ERROR_SOURCE,
        action: 'fetchRoles',
      });
    }
  }

  private async fetchCurrUserRoles(): Promise<void> {
    try {
      const auth0User = await firstValueFrom(this.authService.user$);
      const auth0Id = auth0User?.sub;
      if (!auth0Id) return;

      const user = await firstValueFrom(
        this.userService.getUserbyAuth0Id(auth0Id),
      );
      const userId = user?._id;
      if (!userId) return;

      const assignments = await firstValueFrom(
        this.userPlantRoleService.getUserRoles(userId),
      );
      assignments.forEach((assignment: UserPlantRole) => {
        const role = this.allRoles().find((r) => r._id === assignment.roleId);
        if (role) {
          this.plantRoleMap.update((map) =>
            new Map(map).set(assignment.plantId, role.name),
          );
        }
      });

      this.allProjects.update((items) => this.applyRoles(items));
    } catch (err) {
      this.errorService.report(err, {
        source: ERROR_SOURCE,
        action: 'fetchCurrUserRoles',
      });
    }
  }

  private applyRoles(items: PlantSwitcherItem[]): PlantSwitcherItem[] {
    return items.map((item) => {
      if (item.plantMeta?.id) {
        return {
          ...item,
          role: this.plantRoleMap().get(item.plantMeta.id) ?? '-',
        };
      }
      const plantIds = item.plantIds;
      if (
        plantIds &&
        plantIds.length > 0 &&
        plantIds.every((id) => this.plantRoleMap().has(id))
      ) {
        return { ...item, role: this.plantRoleMap().get(plantIds[0]) ?? '-' };
      }
      return { ...item, role: '-' };
    });
  }

  private toSwitcherItem(p: Project): PlantSwitcherItem {
    return {
      id: p.id,
      name: p.projectName ?? '',
      isMultiPlant: p.isMultiPlant,
      isHealthLoading: !!p.plantMeta && !p.plantMeta.health,
      status: p.plantMeta?.health?.status ?? null,
      role: '-',
      plantMeta: p.plantMeta,
      plantIds: p.plantIds,
    };
  }

  openPlant(item: PlantSwitcherItem, event?: MouseEvent): void {
    // (auxclick) also fires for the right button: leave the context menu alone.
    if (event?.type === 'auxclick' && event.button !== 1) return;

    if (item.plantIds?.length) {
      const hasFullAccess = item.plantIds.every((id) =>
        this.plantRoleMap().has(id),
      );
      if (!hasFullAccess) return;
    }

    if (item.plantMeta?.id && !this.plantRoleMap().has(item.plantMeta.id)) {
      return;
    }

    // A new browser tab, not an in-app tab: the open plant is app-wide, so
    // every in-app tab shares it. The switcher stays open for the next one.
    if (event && isOpenInNewTabClick(event)) {
      globalThis.open(
        this.projectService.newTabUrl(item.id),
        '_blank',
        'noopener',
      );
      return;
    }

    void this.projectService.openProject(item.id, false, false);
    this.closed.emit();
  }

  navigateToDetailedList(): void {
    void this.router.navigate(['/plants']);
    this.closed.emit();
  }
}
